import { Injectable } from '@angular/core';
import { DeviceFullDTO, DeviceOnlineStatus } from '../api/api';
import { TranslateService } from '@ngx-translate/core';
import { DateTime, Interval } from 'luxon';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  constructor(public translate: TranslateService) {}

  status(device: DeviceFullDTO) {
    const devConnected: boolean = device?.connected;

    if (devConnected) {
      return 'CONNECTED';
    }

    if (device?.onlineStatus === DeviceOnlineStatus.Unknown) {
      if (device?.external || device?.offline) {
        return 'EXTERNAL|OFFLINE';
      } else {
        return 'PAIR';
      }
    } else if (device?.lastOnline) {
      return 'ONLINETIME';
    } else {
      return 'ONLINETIMEOFFLINE';
    }
  }

  lastOnlineTitle(device: DeviceFullDTO) {
    switch (this.status(device)) {
      case 'EXTERNAL|OFFLINE':
        return (
          (device?.offline ? this.translate.instant('lang.globals.noSim') : '') +
          (device?.external ? this.translate.instant('lang.globals.external') : '')
        );
      case 'CONNECTED':
        return this.translate.instant('lang.globals.connected');
      case 'ONLINETIME':
        return device.lastOnline.value.toFormat('dd.MM.yy HH:mm:ss');
      case 'ONLINETIMEOFFLINE':
        return this.translate.instant('lang.globals.offline');
      case 'PAIR':
        return this.translate.instant('lang.globals.pair');
      default:
        return '???????';
    }
  }

  lastOnline(device: DeviceFullDTO) {
    if (device) {
      switch (this.status(device)) {
        case 'EXTERNAL|OFFLINE':
          return (
            (device?.offline ? this.translate.instant('lang.globals.noSim') : '') +
            (device?.external ? this.translate.instant('lang.globals.external') : '')
          );
        case 'CONNECTED':
          return this.translate.instant('lang.globals.connected');
        case 'ONLINETIME':
          const duration = DateTime.local().diff(device.lastOnline.value, ['days', 'hours', 'minutes']).toObject();
          duration.minutes = Math.round(duration.minutes);
          if (duration) {
            if (duration.days > 0) {
              return `${duration.days} d ${duration.hours} hrs`;
            } else if (duration.hours > 0) {
              return `${duration.hours} hrs ${duration.minutes} mins`;
            } else if (duration.minutes > 0) {
              return `${duration.minutes} mins`;
            } else {
              return this.translate.instant('lang.globals.now');
            }
          }
          return '???';
        case 'ONLINETIMEOFFLINE':
          return this.translate.instant('lang.globals.offline');
        case 'PAIR':
          return this.translate.instant('lang.globals.pair');
        default:
          return '???????';
      }
    }
  }

  icon(device: DeviceFullDTO) {
    if (device) {
      switch (device?.onlineStatus) {
        case DeviceOnlineStatus.Unknown:
          return 'questionmark';
        case DeviceOnlineStatus.Online:
          return 'online';
        case DeviceOnlineStatus.CurrentlyOffline:
          return 'offlineCurrently';
        case DeviceOnlineStatus.PlannedOffline:
          return 'offlinePlanned';
        case DeviceOnlineStatus.Offline:
          return 'offline';
        default:
          return 'questionmark';
      }
    }
  }

  iconColor(device: DeviceFullDTO) {
    switch (device?.onlineStatus) {
      case DeviceOnlineStatus.Unknown:
        return 'grey';
      case DeviceOnlineStatus.Online:
        return 'green';
      case DeviceOnlineStatus.CurrentlyOffline:
        return 'yellow';
      case DeviceOnlineStatus.PlannedOffline:
        return 'orange';
      case DeviceOnlineStatus.Offline:
        return 'red';
      default:
        return 'blue';
    }
  }

  iconText(device: DeviceFullDTO) {
    switch (device?.onlineStatus) {
      case DeviceOnlineStatus.Unknown:
        return this.translate.instant('lang.globals.unknown.short');
      case DeviceOnlineStatus.Online:
        return this.translate.instant('lang.globals.online.short');
      case DeviceOnlineStatus.CurrentlyOffline:
        return this.translate.instant('lang.globals.recOnline.short');
      case DeviceOnlineStatus.PlannedOffline:
        return this.translate.instant('lang.globals.plannedOffline.short');
      case DeviceOnlineStatus.Offline:
        return this.translate.instant('lang.globals.offline.short');
      default:
        return '???????';
    }
  }

  iconTooltip(device: DeviceFullDTO) {
    switch (device?.onlineStatus) {
      case DeviceOnlineStatus.Unknown:
        return this.translate.instant('lang.globals.unknown');
      case DeviceOnlineStatus.Online:
        return this.translate.instant('lang.globals.online');
      case DeviceOnlineStatus.CurrentlyOffline:
        return this.translate.instant('lang.globals.recOnline');
      case DeviceOnlineStatus.PlannedOffline:
        return this.translate.instant('lang.globals.plannedOffline');
      case DeviceOnlineStatus.Offline:
        return this.translate.instant('lang.globals.offline');
      default:
        return '???????';
    }
  }
}
